/**
 * Cart Drawer for Shopify — Impulse Theme Compatible
 *
 * Features:
 * - Slide-out cart from right side
 * - Tiered discount progress bar ($5/$10/$20 off)
 * - Auto free gift when cart reaches N items (gift is a £0-priced product,
 *   so NO discount / BXGY rule is needed — checkout shows it as free)
 * - Quantity adjustment (increase/decrease/remove)
 * - AJAX cart updates (no page reload)
 * - Hooks into Impulse theme's cart system
 *
 * Configuration: edit TIERS array + GIFT settings below.
 */

(function () {
  'use strict';

  // ─── Configuration ───
  // Item-count based tiers. `threshold` = number of (non-gift) items in cart,
  // `discount` = percentage off shown on the progress bar.
  var TIERS = [
    { threshold: 2, discount: 10 },
    { threshold: 3, discount: 15 },
    { threshold: 4, discount: 20 },
  ];

  // ─── Free Gift Configuration ───────────────────────────────────
  // 1) The VARIANT ID of the mystery gift product.
  //    This is the "🎁 Mystery Gift (100% off)" product priced at £0.00,
  //    so it needs NO discount rule — it is simply free at checkout.
  var GIFT_VARIANT_ID = 56964806345028; // 🎁 Mystery Gift (100% off) — variant (product 15603765936452)

  // 2) How many (non-gift) items in the cart unlock the gift.
  var GIFT_TRIGGER_QTY = 3;

  // 3) Which tier marker shows the "+ free gift" label (0 = first tier,
  //    1 = second/middle tier, 2 = third). Cosmetic only.
  var GIFT_TIER_INDEX = 1;

  var GIFT_LABEL_HTML = '+ free gift 🎁';
  // ───────────────────────────────────────────────────────────────

  var MONEY_FORMAT = window.theme?.settings?.moneyFormat || window.theme?.moneyFormat || window.Shopify?.money_format || '${{amount}}';

  // Guard so gift add/remove doesn't re-trigger itself through the
  // fetch/XHR interceptors below.
  var giftBusy = false;

  // ─── DOM Elements ───
  var drawer = document.getElementById('cart-drawer');
  var overlay = document.getElementById('cart-drawer-overlay');
  var closeBtn = document.getElementById('cart-drawer-close');
  var itemsContainer = document.getElementById('cart-drawer-items');
  var emptyState = document.getElementById('cart-drawer-empty');
  var footer = document.getElementById('cart-drawer-footer');
  var subtotalEl = document.getElementById('cart-subtotal');
  // (Progress bar elements are built dynamically by the progress module below.)

  if (!drawer || !overlay) return;

  // ─── Inject minimal styles for the gift label (green, like reference) ───
  (function injectGiftStyles() {
    if (document.getElementById('cart-gift-label-styles')) return;
    var style = document.createElement('style');
    style.id = 'cart-gift-label-styles';
    style.textContent =
      '.cart-drawer__gift-label{display:block;margin-top:2px;font-size:11px;' +
      'font-weight:600;color:#22a06b;line-height:1;white-space:nowrap;}';
    document.head.appendChild(style);
  })();

  // ─── Self-contained progress bar styles ───
  (function injectProgressStyles() {
    if (document.getElementById('cart-progress-styles')) return;
    var style = document.createElement('style');
    style.id = 'cart-progress-styles';
    style.textContent = [
      '.cdpb{padding:14px 18px 20px;}',
      '.cdpb__text{text-align:center;font-size:13px;line-height:1.3;margin:0 0 14px;color:#303030;}',
      '.cdpb__text strong{font-weight:700;}',
      '.cdpb__track{position:relative;height:6px;border-radius:6px;background:#e6e1d8;margin:0 6px 8px;}',
      '.cdpb__fill{position:absolute;left:0;top:0;height:100%;border-radius:6px;background:#8A8175;width:0;transition:width .35s ease;}',
      '.cdpb__dot{position:absolute;top:50%;left:0;width:14px;height:14px;border-radius:50%;background:#8A8175;border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.12);transform:translate(-50%,-50%);transition:left .35s ease;z-index:2;}',
      '.cdpb__markers{position:relative;height:auto;margin:0 6px;}',
      '.cdpb__marker{position:absolute;top:0;transform:translateX(-50%);text-align:center;width:80px;}',
      '.cdpb__marker--first{transform:translateX(-10%);text-align:left;width:70px;}',
      '.cdpb__marker--last{transform:translateX(-90%);text-align:right;width:70px;}',
      '.cdpb__node{display:inline-block;width:14px;height:14px;border-radius:4px;background:#cfc9bd;position:relative;top:-15px;transition:background .3s ease;}',
      '.cdpb__marker.is-reached .cdpb__node{background:#8A8175;}',
      '.cdpb__label{display:block;font-size:12px;font-weight:700;color:#303030;line-height:1.15;margin-top:-8px;}',
      '.cdpb__gift{display:block;font-size:11px;font-weight:600;color:#22a06b;line-height:1;margin-top:2px;white-space:nowrap;}'
    ].join('');
    document.head.appendChild(style);
  })();

  // ─── Open / Close ───
  function openDrawer() {
    // Close Impulse's own drawers first if any are open
    var impulseDrawers = document.querySelectorAll('.js-drawer-close');
    impulseDrawers.forEach(function (btn) {
      if (btn.closest('.drawer') && btn.closest('.drawer').classList.contains('is-open')) {
        btn.click();
      }
    });

    drawer.classList.add('is-open');
    overlay.classList.add('is-active');
    document.body.classList.add('cart-drawer-open');
    drawer.setAttribute('aria-hidden', 'false');
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-active');
    document.body.classList.remove('cart-drawer-open');
    drawer.setAttribute('aria-hidden', 'true');
  }

  closeBtn.addEventListener('click', closeDrawer);
  overlay.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  // ─── Money formatting ───
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    return MONEY_FORMAT.replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace(
        '{{amount_no_decimals_with_comma_separator}}',
        Math.round(cents / 100)
      );
  }

  // ─── Free-gift helpers ───
  function countNonGiftItems(cart) {
    var count = 0;
    (cart.items || []).forEach(function (item) {
      if (item.variant_id !== GIFT_VARIANT_ID) count += item.quantity;
    });
    return count;
  }

  function findGiftLine(cart) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].variant_id === GIFT_VARIANT_ID) {
        return { line: i + 1, item: items[i] };
      }
    }
    return null;
  }

  function changeGiftLine(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    }).then(function (r) { return r.json(); });
  }

  // Adds / removes / clamps the gift, then returns the resulting cart.
  function maybeUpdateGift(cart) {
    if (giftBusy || !GIFT_VARIANT_ID) {
      return Promise.resolve(cart);
    }

    var qty = countNonGiftItems(cart);
    var gift = findGiftLine(cart);

    // Unlock: add the gift
    if (qty >= GIFT_TRIGGER_QTY && !gift) {
      giftBusy = true;
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: GIFT_VARIANT_ID, quantity: 1 }),
      })
        .then(fetchCart)
        .then(function (c) { giftBusy = false; return c; })
        .catch(function () { giftBusy = false; return cart; });
    }

    // Locked again: remove the gift
    if (qty < GIFT_TRIGGER_QTY && gift) {
      giftBusy = true;
      return changeGiftLine(gift.line, 0)
        .then(function (c) { giftBusy = false; return c; })
        .catch(function () { giftBusy = false; return cart; });
    }

    // Keep the gift pinned at exactly 1
    if (gift && gift.item.quantity !== 1) {
      giftBusy = true;
      return changeGiftLine(gift.line, 1)
        .then(function (c) { giftBusy = false; return c; })
        .catch(function () { giftBusy = false; return cart; });
    }

    return Promise.resolve(cart);
  }

  // ─── Progress bar (fully self-contained) ───
  // Builds its own markup inside #cart-progress so it no longer depends on
  // the theme's hardcoded "$5 OFF / $10 OFF" labels or tier IDs.
  // Driven by ITEM COUNT of non-gift items.

  // Which tier index shows the "+ free gift" sub-label (0-based). Middle tier.
  var GIFT_LABEL_TIER_INDEX = 1;

  function getProgressRoot() {
    var root = document.getElementById('cart-progress');
    if (!root) {
      // Fall back: create a container just above the items list.
      root = document.createElement('div');
      root.id = 'cart-progress';
      if (itemsContainer && itemsContainer.parentNode) {
        itemsContainer.parentNode.insertBefore(root, itemsContainer);
      } else {
        drawer.insertBefore(root, drawer.firstChild);
      }
    }
    return root;
  }

  // Build the markup once; subsequent calls just update it.
  function buildProgressBar() {
    var root = getProgressRoot();
    if (root.querySelector('.cdpb')) return root; // already built

    var segCount = TIERS.length;
    var markersHtml = '';
    TIERS.forEach(function (tier, i) {
      // Position each marker at the end of its segment.
      var pos = ((i + 1) / segCount) * 100;
      var cls = 'cdpb__marker';
      if (i === 0) cls += ' cdpb__marker--first';
      if (i === segCount - 1) cls += ' cdpb__marker--last';
      var giftHtml = (i === GIFT_LABEL_TIER_INDEX)
        ? '<span class="cdpb__gift">' + GIFT_LABEL_HTML + '</span>'
        : '';
      markersHtml +=
        '<div class="' + cls + '" data-tier="' + i + '" style="left:' + pos + '%;">' +
          '<span class="cdpb__node"></span>' +
          '<span class="cdpb__label">' + tier.discount + '% OFF</span>' +
          giftHtml +
        '</div>';
    });

    root.innerHTML =
      '<div class="cdpb">' +
        '<p class="cdpb__text" id="cdpb-text"></p>' +
        '<div class="cdpb__track">' +
          '<div class="cdpb__fill" id="cdpb-fill"></div>' +
          '<div class="cdpb__dot" id="cdpb-dot"></div>' +
        '</div>' +
        '<div class="cdpb__markers" style="height:44px;">' + markersHtml + '</div>' +
      '</div>';

    return root;
  }

  function updateProgress(itemCount) {
    var count = itemCount || 0;
    var root = buildProgressBar();

    var fill = root.querySelector('#cdpb-fill');
    var dot = root.querySelector('#cdpb-dot');
    var text = root.querySelector('#cdpb-text');
    var markers = root.querySelectorAll('.cdpb__marker');

    var segCount = TIERS.length;
    var segWidth = 100 / segCount;

    var nextTierIndex = TIERS.findIndex(function (t) {
      return count < t.threshold;
    });

    // Fill: 0% at 0 items, ramps toward each marker, 100% once final tier hit.
    var fillPercent;
    if (nextTierIndex === -1) {
      fillPercent = 100;
    } else if (nextTierIndex === 0) {
      fillPercent = (count / TIERS[0].threshold) * segWidth;
    } else {
      var prev = TIERS[nextTierIndex - 1].threshold;
      var next = TIERS[nextTierIndex].threshold;
      var segProgress = (count - prev) / (next - prev);
      fillPercent = (nextTierIndex * segWidth) + (segProgress * segWidth);
    }
    fillPercent = Math.max(0, Math.min(fillPercent, 100));

    if (fill) fill.style.width = fillPercent + '%';
    if (dot) dot.style.left = fillPercent + '%';

    // Mark tiers reached / not reached.
    markers.forEach(function (m) {
      var i = parseInt(m.getAttribute('data-tier'), 10);
      if (count >= TIERS[i].threshold) {
        m.classList.add('is-reached');
      } else {
        m.classList.remove('is-reached');
      }
    });

    // Progress text.
    if (text) {
      if (count === 0) {
        var firstRemaining = TIERS[0].threshold;
        text.innerHTML =
          'Add <strong>' + firstRemaining +
          (firstRemaining === 1 ? ' item' : ' items') +
          '</strong> to unlock <strong>' + TIERS[0].discount + '% OFF</strong>';
      } else if (nextTierIndex === -1) {
        text.innerHTML =
          "You've unlocked <strong>" + TIERS[TIERS.length - 1].discount + '% OFF</strong>!';
      } else {
        var remaining = TIERS[nextTierIndex].threshold - count;
        text.innerHTML =
          'Add <strong>' + remaining +
          (remaining === 1 ? ' item' : ' items') +
          '</strong> to unlock <strong>' + TIERS[nextTierIndex].discount + '% OFF</strong>';
      }
    }
  }

  // Injects the "+ free gift" sub-label beneath the configured tier marker.
  function ensureGiftLabel() {
    if (document.querySelector('.cart-drawer__gift-label')) return;
    var icon = document.getElementById('tier-' + (GIFT_TIER_INDEX + 1) + '-icon');
    if (!icon) return;
    // Attach to the tier's label wrapper (fall back to the icon's parent)
    var host = icon.closest('[id^="tier-"]') || icon.parentElement;
    if (!host) return;
    var label = document.createElement('span');
    label.className = 'cart-drawer__gift-label';
    label.innerHTML = GIFT_LABEL_HTML;
    host.appendChild(label);
  }

  // ─── Compare-price cache (variant_id -> compare_at_price cents, or null) ───
  var comparePriceCache = {};
  var productFetchPromises = {};

  function ensureComparePrices(items) {
    var pending = [];
    var seenHandles = {};
    items.forEach(function (item) {
      if (comparePriceCache[item.variant_id] !== undefined) return;
      if (!item.handle || seenHandles[item.handle]) return;
      seenHandles[item.handle] = true;

      var handle = item.handle;
      if (!productFetchPromises[handle]) {
        productFetchPromises[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (product) {
            (product.variants || []).forEach(function (v) {
              comparePriceCache[v.id] = v.compare_at_price || null;
            });
          })
          .catch(function () { /* swallow */ });
      }
      pending.push(productFetchPromises[handle]);
    });
    return Promise.all(pending);
  }

  function buildPriceHtml(item) {
    // Gift line always shows as FREE
    if (item.variant_id === GIFT_VARIANT_ID || item.final_line_price === 0) {
      return '<span class="cart-drawer__item-price cart-drawer__item-price--free">FREE</span>';
    }
    var compareUnit = comparePriceCache[item.variant_id];
    if (compareUnit && compareUnit > item.final_price) {
      var lineCompare = compareUnit * item.quantity;
      var savingsPct = Math.round(((compareUnit - item.final_price) / compareUnit) * 100);
      return (
        '<div class="cart-drawer__item-prices">' +
          '<s class="cart-drawer__item-compare">' + formatMoney(lineCompare) + '</s>' +
          '<strong class="cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</strong>' +
          '<span class="cart-drawer__item-savings">-' + savingsPct + '%</span>' +
        '</div>'
      );
    }
    return '<span class="cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</span>';
  }

  // ─── Render cart items ───
  function renderCart(cart) {
    // First reconcile the free gift, then render the resulting cart.
    maybeUpdateGift(cart).then(function (finalCart) {
      // Progress bar / subtotal / bubble update IMMEDIATELY
      subtotalEl.textContent = formatMoney(finalCart.total_price);
      updateProgress(countNonGiftItems(finalCart));
      updateCartBubble(finalCart.item_count);

      // Items need compare-prices first (fetched once per product handle)
      ensureComparePrices(finalCart.items || []).then(function () {
        renderCartItems(finalCart);
      });
    });
  }

  function renderCartItems(cart) {
    if (cart.item_count === 0) {
      itemsContainer.innerHTML = '';
      emptyState.classList.remove('cart-drawer__empty--hidden');
      footer.classList.add('cart-drawer__footer--hidden');
      document.getElementById('cart-progress').style.display = 'none';
    } else {
      emptyState.classList.add('cart-drawer__empty--hidden');
      footer.classList.remove('cart-drawer__footer--hidden');
      document.getElementById('cart-progress').style.display = '';

      var html = '';
      cart.items.forEach(function (item, index) {
        var isGift = item.variant_id === GIFT_VARIANT_ID;

        var imgSrc = '';
        if (item.featured_image) {
          var imgUrl = item.featured_image.url || item.featured_image;
          imgSrc = imgUrl.replace(/(\.\w+)(\?.*)?$/, '_160x$1$2');
        } else if (item.image) {
          imgSrc = item.image.replace(/(\.\w+)(\?.*)?$/, '_160x$1$2');
        }

        var variantTitle =
          item.variant_title && item.variant_title !== 'Default Title'
            ? '<p class="cart-drawer__item-variant">' +
              escapeHtml(item.variant_title) +
              '</p>'
            : '';

        var giftBadge = isGift
          ? '<span class="cart-drawer__item-gift-badge">🎁 Free gift</span>'
          : '';

        // Gift line: no quantity controls, no remove (it's auto-managed)
        var controls = isGift
          ? '<div class="cart-drawer__quantity cart-drawer__quantity--locked">Qty 1</div>'
          : '<div class="cart-drawer__quantity">' +
              '<button class="cart-drawer__qty-btn" data-action="decrease" data-line="' +
              (index + 1) +
              '" aria-label="Decrease quantity">−</button>' +
              '<input type="number" class="cart-drawer__qty-input" value="' +
              item.quantity +
              '" min="0" data-line="' +
              (index + 1) +
              '" aria-label="Quantity">' +
              '<button class="cart-drawer__qty-btn" data-action="increase" data-line="' +
              (index + 1) +
              '" aria-label="Increase quantity">+</button>' +
            '</div>';

        html +=
          '<div class="cart-drawer__item' + (isGift ? ' cart-drawer__item--gift' : '') + '" data-line="' +
          (index + 1) +
          '" data-key="' +
          item.key +
          '">' +
          '<a href="' +
          item.url +
          '" class="cart-drawer__item-image">' +
          '<img src="' +
          imgSrc +
          '" alt="' +
          escapeHtml(item.product_title) +
          '" width="80" height="80" loading="lazy">' +
          '</a>' +
          '<div class="cart-drawer__item-details">' +
          '<a href="' +
          item.url +
          '" class="cart-drawer__item-title">' +
          escapeHtml(item.product_title) +
          '</a>' +
          giftBadge +
          variantTitle +
          '<div class="cart-drawer__item-bottom">' +
          controls +
          buildPriceHtml(item) +
          '</div>' +
          '</div>' +
          '</div>';
      });
      itemsContainer.innerHTML = html;
    }
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ─── Cart API helpers ───
  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      });
  }

  function changeLineItem(line, quantity) {
    drawer.classList.add('is-loading');
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        renderCart(cart);
        drawer.classList.remove('is-loading');
        return cart;
      })
      .catch(function () {
        drawer.classList.remove('is-loading');
      });
  }

  // ─── Event delegation for quantity buttons ───
  itemsContainer.addEventListener('click', function (e) {
    var btn = e.target.closest('.cart-drawer__qty-btn');
    if (!btn) return;

    var line = parseInt(btn.dataset.line, 10);
    var input = btn.parentElement.querySelector('.cart-drawer__qty-input');
    var currentQty = parseInt(input.value, 10);

    if (btn.dataset.action === 'decrease') {
      changeLineItem(line, Math.max(0, currentQty - 1));
    } else if (btn.dataset.action === 'increase') {
      changeLineItem(line, currentQty + 1);
    }
  });

  itemsContainer.addEventListener('change', function (e) {
    if (e.target.classList.contains('cart-drawer__qty-input')) {
      var line = parseInt(e.target.dataset.line, 10);
      var qty = parseInt(e.target.value, 10);
      if (!isNaN(qty) && qty >= 0) {
        changeLineItem(line, qty);
      }
    }
  });

  // ─── Update header cart icon bubble ───
  function updateCartBubble(count) {
    var selectors = [
      '.cart-link__bubble-num',
      '.cart-link__count',
      '.cart-count',
      '.cart-count-bubble span',
      '[data-cart-count]',
      '.site-header__cart-count',
      '#cart-icon-bubble span',
      '#CartCount',
    ];
    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.textContent = count;
      });
    });

    // Show/hide bubble
    document.querySelectorAll('.cart-link__bubble').forEach(function (el) {
      if (count > 0) {
        el.classList.add('cart-link__bubble--visible');
      } else {
        el.classList.remove('cart-link__bubble--visible');
      }
    });
  }

  // ─── Monkey-patch fetch to intercept Impulse's AJAX add-to-cart ───
  var originalFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];

    var isAddToCart = false;
    if (typeof url === 'string' && url.indexOf('/cart/add') !== -1) {
      isAddToCart = true;
    } else if (url && url.url && url.url.indexOf('/cart/add') !== -1) {
      isAddToCart = true;
    }

    var result = originalFetch.apply(this, arguments);

    // Skip our own gift add — maybeUpdateGift already re-renders.
    if (isAddToCart && !giftBusy) {
      result.then(function () {
        setTimeout(function () {
          fetchCart().then(function (cart) {
            renderCart(cart);
            openDrawer();
          });
        }, 300);
      });
    }

    return result;
  };

  // ─── Also intercept XMLHttpRequest for older Impulse versions ───
  var originalXHROpen = XMLHttpRequest.prototype.open;
  var originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function () {
    this._cartDrawerUrl = arguments[1] || '';
    return originalXHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (xhr._cartDrawerUrl && xhr._cartDrawerUrl.indexOf('/cart/add') !== -1) {
      xhr.addEventListener('load', function () {
        if (giftBusy) return;
        setTimeout(function () {
          fetchCart().then(function (cart) {
            renderCart(cart);
            openDrawer();
          });
        }, 300);
      });
    }
    return originalXHRSend.apply(this, arguments);
  };

  // ─── Intercept add-to-cart form submissions ───
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (
      form.action &&
      form.action.indexOf('/cart/add') !== -1
    ) {
      e.preventDefault();
      e.stopPropagation();

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        body: new FormData(form),
      })
        .then(function (r) { return r.json(); })
        .then(function () { return fetchCart(); })
        .then(function (cart) {
          renderCart(cart);
          openDrawer();
        });
    }
  }, true);

  // ─── Open cart triggers (Impulse theme selectors) ───
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest(
      '[data-cart-drawer-toggle], ' +
      '.js-drawer-open-cart, ' +
      'a[href="/cart"], ' +
      '.site-header__cart, ' +
      '.cart-link, ' +
      '.header-cart, ' +
      '[data-cart-toggle], ' +
      '.js-cart-link'
    );
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      fetchCart().then(function (cart) {
        renderCart(cart);
        openDrawer();
      });
    }
  }, true);

  // ─── Listen for Impulse's custom cart events ───
  document.addEventListener('cart:updated', function () {
    fetchCart().then(function (cart) {
      renderCart(cart);
    });
  });

  document.addEventListener('ajaxProduct:added', function () {
    fetchCart().then(function (cart) {
      renderCart(cart);
      openDrawer();
    });
  });

  // ─── Disable Impulse's built-in cart drawer ───
  if (window.theme && window.theme.cart) {
    window.theme.cart.open = function () {
      fetchCart().then(function (cart) {
        renderCart(cart);
        openDrawer();
      });
    };
  }

  // ─── Make openDrawer available globally ───
  window.cartDrawerOpen = openDrawer;
  window.cartDrawerClose = closeDrawer;

  // ─── Initial render on page load ───
  fetchCart().then(function (cart) {
    renderCart(cart);
  });
})();